import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ApiError } from '../api/client'
import { useAuth } from '../auth/useAuth'
import { ProgressBar } from '../components/ProgressBar'
import {
  EVENT_STATUSES,
  formatDateRange,
  useEvents,
  type EventStatus,
} from '../features/events/api'

export function EventsPage() {
  const { can } = useAuth()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<EventStatus | ''>('')
  const [includeArchived, setIncludeArchived] = useState(false)
  const [needsAccountManager, setNeedsAccountManager] = useState(false)

  const {
    data: events,
    isPending,
    isError,
    error,
  } = useEvents({ search, status, includeArchived, needsAccountManager })

  return (
    <section>
      <div className="page-header">
        <h1>Events</h1>
        {can('Coordinator') ? (
          <Link className="button primary" to="/events/new">
            New event
          </Link>
        ) : null}
      </div>

      <div className="filters">
        <div className="field">
          <label htmlFor="event-search">Search</label>
          <input
            id="event-search"
            type="search"
            value={search}
            placeholder="Name, code, or venue"
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="event-status">Status</label>
          <select
            id="event-status"
            value={status}
            onChange={(event) => setStatus(event.target.value as EventStatus | '')}
          >
            <option value="">Planning and Active</option>
            {EVENT_STATUSES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(event) => setIncludeArchived(event.target.checked)}
          />
          Include archived
        </label>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={needsAccountManager}
            onChange={(event) => setNeedsAccountManager(event.target.checked)}
          />
          Needs an account manager
        </label>
      </div>

      {isError ? (
        <p role="alert" className="error">
          {error instanceof ApiError ? error.detail : 'Could not load events.'}
        </p>
      ) : null}

      {isPending ? <p>Loading events…</p> : null}

      {events && events.length === 0 ? (
        <p className="empty">
          {search || status || includeArchived
            ? 'No events match those filters.'
            : 'No events yet.'}
        </p>
      ) : null}

      {events && events.length > 0 ? (
        <ul className="card-list">
          {events.map((event) => (
            <li key={event.id} className={event.isArchived ? 'card row-inactive' : 'card'}>
              <Link to={`/events/${event.id}`} className="card-title">
                {event.name}
              </Link>
              <div className="card-meta">
                <span className="code">{event.code}</span>
                <span>{formatDateRange(event.startDate, event.endDate)}</span>
                {event.venueName ? <span>{event.venueName}</span> : null}
                {event.venueCity ? <span>{event.venueCity}</span> : null}
                {event.accountManagerDisplayName ? (
                  <span>Account manager: {event.accountManagerDisplayName}</span>
                ) : null}
              </div>
              {event.progress ? (
                <ProgressBar progress={event.progress} label={event.name} />
              ) : null}

              <div className="card-tags">
                <span className={`status status-${event.status.toLowerCase()}`}>
                  {event.status}
                </span>
                {event.needsAccountManager ? (
                  <span className="badge overdue">Needs an account manager</span>
                ) : null}
                {event.hasPlan ? <span className="badge">Has plan</span> : null}
                {event.isArchived ? <span className="badge">Archived</span> : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}
